// Ce que le Deviseur et la page du devis partagent pour parler au relevé :
// le dépôt direct sur S3, la lecture prudente des réponses, la progression.

// Le dépôt part directement vers l'API, sans le proxy Netlify : celui-ci refuse
// tout envoi au-delà d'environ 6 Mo (mesuré le 27/09/2026). En local, le proxy
// de développement fait l'affaire.
export const BASE_DEPOT = /apexrenovation\.fr$/.test(window.location.hostname) ? 'https://apex-qfhw.onrender.com' : '';
export const MAX_FICHIERS = 40;

// Une réponse vide ou non JSON — proxy qui coupe, serveur qui redémarre — ne
// doit pas finir en « Unexpected end of JSON input » sous les yeux de qui
// devise. On dit ce qui s'est passé, avec le code.
export async function lireJson(r) {
  const texte = await r.text();
  if (!texte.trim()) {
    return { error: r.status >= 502
      ? `Le serveur n'a pas répondu à temps (${r.status}) — il redémarre peut-être. Réessayez dans une minute.`
      : `Le serveur a répondu ${r.status} sans contenu.` };
  }
  try { return JSON.parse(texte); }
  catch { return { error: `Réponse illisible du serveur (${r.status}).` }; }
}

// Envoi d'un fichier sur une URL présignée S3, avec progression — fetch ne
// sait pas la donner.
export function envoyerSurS3(url, fichier, onProgress) {
  return new Promise((ok, ko) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('Content-Type', fichier.type || 'application/octet-stream');
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
    x.onload = () => (x.status >= 200 && x.status < 300 ? ok() : ko(new Error(`Le stockage a refusé « ${fichier.name} » (${x.status})`)));
    x.onerror = () => ko(new Error(`Envoi de « ${fichier.name} » interrompu — vérifiez la connexion et relancez`));
    x.send(fichier);
  });
}

// Le dépôt direct : le serveur signe une URL par fichier, le navigateur envoie
// lui-même, et on rend les clés à donner à l'analyse. null si le serveur n'a
// pas de stockage (503, en local) : l'appelant retombe sur le multipart.
export async function deposerDirect(fichiers, entetes, surEtape) {
  if (!BASE_DEPOT || !fichiers.length) return null;
  const pre = await fetch(`${BASE_DEPOT}/api/pro/releve/depot`, {
    method: 'POST', headers: entetes,
    body: JSON.stringify({ fichiers: fichiers.map(f => ({ nom: f.name, type: f.type, taille: f.size })) }),
  });
  if (pre.status === 503) return null;
  const plan = await lireJson(pre);
  if (!pre.ok) throw new Error(plan.error || `Préparation du dépôt refusée (${pre.status})`);
  const total = fichiers.reduce((a, f) => a + f.size, 0) || 1;
  let fait = 0;
  for (const [i, f] of fichiers.entries()) {
    await envoyerSurS3(plan.fichiers[i].url, f, (envoye) =>
      surEtape(`Envoi ${i + 1}/${fichiers.length} — ${Math.min(99, Math.round(((fait + envoye) / total) * 100))} %`));
    fait += f.size;
  }
  return plan.fichiers.map(({ key, nom, type, taille }) => ({ key, nom, type, taille }));
}

// Progression : les étapes du serveur n'ont pas toutes une mesure. Chacune
// reçoit une plage de la barre ; l'envoi et l'inspection des photos avancent
// au réel (pourcentage ou k/N dans le libellé), les autres rampent vers la fin
// de leur plage pour que rien ne paraisse figé pendant une transcription de
// dix minutes.
const PLAGES = [
  [/^Envoi/, 0, 25], [/Rapatriement/, 25, 30], [/Transcription/, 30, 50],
  [/Enregistrement des photos/, 50, 55], [/Inspection des photos/, 55, 75],
  [/Interprétation/, 75, 92], [/Chiffrage/, 92, 96], [/Enregistrement du dossier/, 96, 99],
];
export function plageDe(etape) {
  for (const [re, debut, fin] of PLAGES) {
    if (!re.test(etape || '')) continue;
    const pc = (etape.match(/(\d+)\s*%/) || [])[1];
    const kn = etape.match(/(\d+)\s*\/\s*(\d+)/);
    const frac = pc ? Number(pc) / 100 : (kn ? Number(kn[1]) / Number(kn[2]) : null);
    return { debut, fin, exact: frac === null ? null : debut + (fin - debut) * frac };
  }
  return { debut: 0, fin: 100, exact: null };
}
export const duree = (s) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`);

// Suit une tâche d'analyse jusqu'au bout. surEtape reçoit chaque étape.
export async function suivreTache(id, entetes, surEtape) {
  for (;;) {
    await new Promise(ok => setTimeout(ok, 2000));
    const s = await fetch(`${BASE_DEPOT}/api/pro/releve/tache/${id}`, { headers: entetes });
    const suivi = await lireJson(s);
    if (!s.ok) throw new Error(suivi.error || `Suivi impossible (${s.status})`);
    if (suivi.etat === 'erreur') throw new Error(suivi.erreur || 'Analyse échouée');
    if (suivi.etat === 'termine') return suivi.resultat;
    surEtape(suivi.etape || 'Analyse en cours');
  }
}
